import { Link, useParams } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowRight, BriefcaseBusiness, CheckCircle2, ChevronRight, Clock3, Quote } from "lucide-react";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import DiscoveryCallDialog from "@/components/DiscoveryCallDialog";
import SEOHead from "@/components/SEOHead";
import { useCaseStudy, usePublishedCaseStudies } from "@/lib/cms";

const fadeUp = (delay = 0) => ({
  initial: { opacity: 0, y: 24 },
  whileInView: { opacity: 1, y: 0 },
  viewport: { once: true },
  transition: { duration: 0.5, delay },
});

const CaseStudyDetail = () => {
  const { slug = "" } = useParams();
  const { data: caseStudy } = useCaseStudy(slug);
  const { data: allCaseStudies = [] } = usePublishedCaseStudies();
  const related = allCaseStudies.filter((study) => study.slug !== slug).slice(0, 2);

  if (!caseStudy) {
    return (
      <div className="container mx-auto px-4 py-20 text-center">
        <h1 className="text-4xl font-heading font-bold text-foreground">Case Study Not Found</h1>
        <p className="mt-3 text-base text-muted-foreground">
          <Link to="/case-studies" className="text-primary hover:underline">
            ← Back to Case Studies
          </Link>
        </p>
      </div>
    );
  }

  return (
    <>
      <SEOHead
        title={caseStudy.seo.title}
        description={caseStudy.seo.description}
        canonical={caseStudy.seo.canonical}
        ogTitle={caseStudy.seo.ogTitle}
        ogDescription={caseStudy.seo.ogDescription}
        ogImage={caseStudy.seo.ogImage ?? caseStudy.image}
        type={caseStudy.seo.type ?? "article"}
        schema={[
          {
            "@context": "https://schema.org",
            "@type": "Article",
            headline: caseStudy.title,
            description: caseStudy.summary,
            image: caseStudy.image,
            author: { "@type": "Person", name: "Mahirah" },
            publisher: {
              "@type": "Organization",
              name: "MViBE",
              logo: { "@type": "ImageObject", url: "https://mvibeon.com/logo.png" },
            },
            mainEntityOfPage: `https://mvibeon.com/case-studies/${caseStudy.slug}`,
          },
          {
            "@context": "https://schema.org",
            "@type": "BreadcrumbList",
            itemListElement: [
              { "@type": "ListItem", position: 1, name: "Home", item: "https://mvibeon.com/" },
              { "@type": "ListItem", position: 2, name: "Case Studies", item: "https://mvibeon.com/case-studies" },
              { "@type": "ListItem", position: 3, name: caseStudy.title, item: `https://mvibeon.com/case-studies/${caseStudy.slug}` },
            ],
          },
        ]}
      />


      <section className="bg-primary py-12 text-primary-foreground md:py-16">
        <div className="container mx-auto max-w-5xl px-4 text-center">
          <nav className="mb-4 flex items-center justify-center gap-1.5 text-base text-primary-foreground/60">
            <Link to="/" className="transition-colors hover:text-primary-foreground">Home</Link>
            <ChevronRight className="h-4 w-4" />
            <Link to="/case-studies" className="transition-colors hover:text-primary-foreground">Case Studies</Link>
            <ChevronRight className="h-4 w-4" />
            <span className="max-w-[260px] truncate text-primary-foreground">{caseStudy.title}</span>
          </nav>

          <Badge className="mb-4 bg-accent text-accent-foreground">{caseStudy.category}</Badge>
          <motion.h1 {...fadeUp()} className="mx-auto max-w-4xl text-3xl font-heading font-bold leading-tight md:text-5xl lg:text-6xl">
            {caseStudy.title}
          </motion.h1>
          <motion.p {...fadeUp(0.08)} className="mx-auto mt-4 max-w-3xl text-lg text-primary-foreground/80">
            {caseStudy.summary}
          </motion.p>

          <motion.div {...fadeUp(0.16)} className="mt-6 flex flex-wrap justify-center gap-3 text-sm">
            <div className="rounded-full border border-primary-foreground/15 bg-primary-foreground/10 px-4 py-2">
              <span className="font-semibold text-primary-foreground">Client:</span> {caseStudy.clientLabel}
            </div>
            <div className="rounded-full border border-primary-foreground/15 bg-primary-foreground/10 px-4 py-2">
              <span className="font-semibold text-primary-foreground">Engagement:</span> {caseStudy.engagementLabel}
            </div>
            <div className="rounded-full border border-primary-foreground/15 bg-primary-foreground/10 px-4 py-2">
              <span className="font-semibold text-primary-foreground">Duration:</span> {caseStudy.durationLabel}
            </div>
          </motion.div>
        </div>
      </section>

      <section className="bg-background py-12 md:py-16">
        <div className="container mx-auto max-w-5xl px-4">
          <motion.img
            {...fadeUp()}
            src={caseStudy.image}
            alt={caseStudy.title}
            className="h-[280px] w-full rounded-3xl object-cover shadow-sm md:h-[420px]"
            loading="lazy"
          />

          <div className="mt-10 grid gap-6 lg:grid-cols-[1.2fr,0.8fr]">
            <motion.article {...fadeUp(0.06)} className="space-y-8">
              <section className="rounded-3xl border border-border bg-card p-6 shadow-sm md:p-8">
                <h2 className="text-2xl font-heading font-bold text-foreground md:text-3xl">The Challenge</h2>
                <p className="mt-4 text-base leading-8 text-muted-foreground md:text-lg">{caseStudy.challenge}</p>
              </section>

              <section className="rounded-3xl border border-border bg-card p-6 shadow-sm md:p-8">
                <h2 className="text-2xl font-heading font-bold text-foreground md:text-3xl">MVIBE Approach</h2>
                <div className="mt-5 space-y-4">
                  {caseStudy.approach.map((item, index) => (
                    <div key={item} className="flex gap-3">
                      <div className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-accent text-sm font-semibold text-accent-foreground">
                        {index + 1}
                      </div>
                      <p className="text-base leading-7 text-muted-foreground md:text-lg">{item}</p>
                    </div>
                  ))}
                </div>
              </section>

              <section className="rounded-3xl border border-border bg-card p-6 shadow-sm md:p-8">
                <h2 className="text-2xl font-heading font-bold text-foreground md:text-3xl">Solution Delivered</h2>
                <p className="mt-4 text-base leading-8 text-muted-foreground md:text-lg">{caseStudy.solution}</p>
              </section>
            </motion.article>

            <motion.aside {...fadeUp(0.12)} className="space-y-6">
              <section className="rounded-3xl border border-border bg-muted/40 p-6 shadow-sm">
                <div className="flex items-center gap-2 text-foreground">
                  <BriefcaseBusiness className="h-5 w-5 text-accent" />
                  <h2 className="text-xl font-heading font-bold">What changed</h2>
                </div>
                <div className="mt-5 space-y-4">
                  {caseStudy.outcomes.map((outcome) => (
                    <div key={outcome.label} className="rounded-2xl border border-border bg-card p-4">
                      <p className="text-sm font-semibold uppercase tracking-[0.14em] text-muted-foreground">{outcome.label}</p>
                      <p className="mt-2 text-2xl font-heading font-bold text-foreground">{outcome.value}</p>
                      <p className="mt-2 text-sm leading-6 text-muted-foreground">{outcome.detail}</p>
                    </div>
                  ))}
                </div>
              </section>

              {caseStudy.testimonialQuote ? (
                <section className="rounded-3xl border border-border bg-card p-6 shadow-sm">
                  <Quote className="h-6 w-6 text-accent" />
                  <p className="mt-4 text-lg font-medium leading-8 text-foreground">“{caseStudy.testimonialQuote}”</p>
                  {caseStudy.testimonialAttribution ? (
                    <p className="mt-3 text-sm font-semibold uppercase tracking-[0.14em] text-muted-foreground">
                      {caseStudy.testimonialAttribution}
                    </p>
                  ) : null}
                </section>
              ) : null}
            </motion.aside>
          </div>
        </div>
      </section>

      <section className="bg-muted/30 py-12 md:py-16">
        <div className="container mx-auto max-w-5xl px-4">
          <div className="grid gap-6 md:grid-cols-[1fr,1fr]">
            <motion.section {...fadeUp()} className="rounded-3xl border border-border bg-card p-6 shadow-sm md:p-8">
              <div className="flex items-center gap-2 text-foreground">
                <Clock3 className="h-5 w-5 text-accent" />
                <h2 className="text-2xl font-heading font-bold">Key Takeaways</h2>
              </div>
              <ul className="mt-5 space-y-4">
                {caseStudy.keyTakeaways.map((item) => (
                  <li key={item} className="flex gap-3">
                    <CheckCircle2 className="mt-1 h-5 w-5 shrink-0 text-accent" />
                    <span className="text-base leading-7 text-muted-foreground">{item}</span>
                  </li>
                ))}
              </ul>
            </motion.section>

            <motion.section {...fadeUp(0.08)} className="rounded-3xl bg-accent p-6 shadow-sm md:p-8">
              <h2 className="text-2xl font-heading font-bold text-accent-foreground">{caseStudy.ctaTitle}</h2>
              <p className="mt-4 text-base leading-7 text-accent-foreground/85">{caseStudy.ctaDescription}</p>
              <DiscoveryCallDialog>
                <Button size="lg" className="mt-6 bg-primary text-primary-foreground hover:bg-primary/90">
                  Book a Discovery Call
                </Button>
              </DiscoveryCallDialog>
            </motion.section>
          </div>
        </div>
      </section>

      {related.length ? (
        <section className="bg-background py-12 md:py-16">
          <div className="container mx-auto max-w-5xl px-4">
            <h2 className="text-center text-2xl font-heading font-bold text-foreground md:text-3xl">Related Case Studies</h2>
            <div className="mt-8 grid gap-6 md:grid-cols-2">
              {related.map((study, index) => (
                <motion.article
                  key={study.slug}
                  {...fadeUp(index * 0.08)}
                  whileHover={{ y: -4 }}
                  className="overflow-hidden rounded-3xl border border-border bg-card shadow-sm"
                >
                  <img src={study.image} alt={study.title} className="h-48 w-full object-cover" loading="lazy" />
                  <div className="p-6">
                    <Badge className="bg-accent text-accent-foreground">{study.category}</Badge>
                    <h3 className="mt-3 text-xl font-heading font-bold text-foreground">{study.title}</h3>
                    <p className="mt-3 text-base leading-7 text-muted-foreground">{study.summary}</p>
                    <Link to={`/case-studies/${study.slug}`} className="mt-4 inline-flex items-center gap-2 text-base font-semibold text-primary hover:underline">
                      Read full case study <ArrowRight className="h-4 w-4" />
                    </Link>
                  </div>
                </motion.article>
              ))}
            </div>
          </div>
        </section>
      ) : null}
    </>
  );
};

export default CaseStudyDetail;
